import React, { useEffect, useId, useState, type ReactNode, type Ref } from 'react';
import { getFormattedPrice } from '@tools/utils/common.tsx';

export type CartSummaryActionVariant = 'primary' | 'secondary' | 'ghost';

export interface CartSummaryAction {
  key: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  icon?: ReactNode;
  variant?: CartSummaryActionVariant;
  buttonRef?: Ref<HTMLButtonElement>;
}

interface CartSummaryBarProps {
  total: number;
  currency?: string;
  primaryAction: CartSummaryAction;
  secondaryActions?: CartSummaryAction[];
  meta?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  subtotal?: number;
  discountAmount?: number;
  balanceAmount?: number;
  balanceDraft?: string;
  onBalanceChange?: (value: string) => void;
  balanceError?: string | null;
  balanceAvailable?: number;
  balanceDebt?: number;
  defaultExpanded?: boolean;
  isMainPanel?: boolean;
  itemCount?: number;
  onOpenCheckout?: () => void;
}

export const CartSummaryBar: React.FC<CartSummaryBarProps> = ({
  total,
  currency,
  primaryAction,
  secondaryActions = [],
  meta,
  footer,
  children,
  balanceAmount,
  balanceDraft,
  onBalanceChange,
  balanceError,
  balanceAvailable,
  balanceDebt,
  itemCount = 0,
  onOpenCheckout,
}) => {
  const balanceInputId = useId();
  const [mode, setMode] = useState<'credit' | 'debt'>('credit');
  const [draft, setDraft] = useState('');

  useEffect(() => {
    const raw = (balanceDraft ?? '').trim();
    setDraft(raw.replace(/^[-+]/, ''));
    if (raw.startsWith('-') || (balanceAmount ?? 0) < 0) setMode('debt');
    else if (raw || Math.abs(balanceAmount ?? 0) > 0.009) setMode('credit');
  }, [balanceAmount, balanceDraft]);

  const applyDraft = (value: string, nextMode = mode) => {
    setDraft(value);
    onBalanceChange?.(value.trim() ? `${nextMode === 'debt' ? '-' : ''}${value.replace(/^[-+]/, '')}` : '');
  };

  const handleMode = (nextMode: 'credit' | 'debt') => {
    setMode(nextMode);
    applyDraft(draft, nextMode);
  };

  const secondary = secondaryActions.filter(Boolean);
  return (
    <section className="pos-cart-summary-bar-v2" role="region" aria-label="Resumen de carrito">
      {onBalanceChange ? (
        <div className="pos-cart-summary-bar-v2__balance-row">
          <div className="pos-cart-summary-bar-v2__balance-label">
            <span>Saldo</span>
            {balanceAvailable != null ? <small>Disponible {getFormattedPrice(Math.max(0, balanceAvailable), currency)}</small> : null}
            {balanceDebt != null && balanceDebt > 0.009 ? <small>Deuda {getFormattedPrice(balanceDebt, currency)}</small> : null}
          </div>
          <div className="pos-cart-summary-bar-v2__balance-controls">
            <button type="button" className={mode === 'credit' ? 'is-active' : ''} onClick={() => handleMode('credit')} aria-pressed={mode === 'credit'}>Usar saldo</button>
            <button type="button" className={mode === 'debt' ? 'is-active' : ''} onClick={() => handleMode('debt')} aria-pressed={mode === 'debt'}>Registrar deuda</button>
            <input id={balanceInputId} type="text" inputMode="decimal" value={draft} onChange={(event) => applyDraft(event.target.value)} placeholder="0,00" aria-invalid={Boolean(balanceError)} />
          </div>
          {balanceError ? <span className="pos-cart-summary-bar-v2__balance-error">{balanceError}</span> : null}
        </div>
      ) : null}

      <div className="pos-cart-summary-bar-v2__main-row">
        <button type="button" className="pos-cart-summary-bar-v2__cart-trigger" onClick={onOpenCheckout} disabled={!onOpenCheckout}>
          <span>Carrito ({itemCount})</span>
          <strong>{getFormattedPrice(total, currency)}</strong>
        </button>
        <div className="pos-cart-summary-bar-v2__actions">
          {secondary.map((action) => (
            <button key={action.key} type="button" className="pos-cart-summary-bar-v2__secondary-action" onClick={action.onClick} disabled={action.disabled}>{action.label}</button>
          ))}
          <button ref={primaryAction.buttonRef} type="button" className="pos-cart-summary-bar-v2__primary-action" onClick={primaryAction.onClick} disabled={primaryAction.disabled} aria-label={primaryAction.ariaLabel ?? primaryAction.label}>
            {primaryAction.icon}{primaryAction.label}
          </button>
        </div>
      </div>
      {meta || children || footer ? <div className="pos-cart-summary-bar-v2__extra">{meta}{children}{footer}</div> : null}
    </section>
  );
};

export default CartSummaryBar;
